import React from 'react';

export default function HolidayTable({
  records = [],
  isLoading = false,
  onResetFilters,
}) {
  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center rounded-2xl border border-outline-variant/20 bg-surface-container-lowest">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="text-xs font-semibold text-on-surface-variant">Loading holiday list...</span>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex h-64 w-full flex-col items-center justify-center rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-8 text-center">
        <span className="material-symbols-outlined mb-2 text-4xl text-outline-variant">
          event_busy
        </span>
        <h3 className="font-display text-sm font-bold text-black">No Holidays Found</h3>
        <p className="mt-1 text-xs text-slate-600 max-w-sm">
          No scheduled holidays match your selected employee group, year, or filter criteria.
        </p>
        {onResetFilters && (
          <button
            type="button"
            onClick={onResetFilters}
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            <span>Reset Filters</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="border-b border-outline-variant/25 bg-surface-container-low/40 text-[11px] font-extrabold uppercase tracking-wider text-black">
              <th className="py-3.5 px-6 text-black">NAME</th>
              <th className="py-3.5 px-6 text-black">GROUP</th>
              <th className="py-3.5 px-6 text-black">DATE</th>
              <th className="py-3.5 px-6 text-black">DAY</th>
              <th className="py-3.5 px-6 text-center text-black">TYPE</th>
              <th className="py-3.5 px-6 text-center text-black">STATUS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item, index) => {
              const isUpcoming = item.status === 'Upcoming';
              return (
                <tr
                  key={item.id || index}
                  className="transition-colors hover:bg-surface-container-low/50 group"
                >
                  {/* NAME */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <div className="flex flex-col">
                      <span className="font-bold text-sm text-black font-sans not-italic">
                        {item.name}
                      </span>
                      {item.description && (
                        <span className="text-[11px] text-slate-500 font-medium mt-0.5">
                          {item.description}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* GROUP */}
                  <td className="py-4 px-6 text-xs text-slate-700 font-medium">
                    {item.groups && Array.isArray(item.groups) ? (
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {item.groups.map((g, gi) => (
                          <span
                            key={gi}
                            className="inline-block rounded-md bg-surface-container-high px-2 py-0.5 text-[10px] font-semibold text-slate-700"
                          >
                            {g}
                          </span>
                        ))}
                      </div>
                    ) : (
                      item.group || 'All Groups'
                    )}
                  </td>

                  {/* DATE */}
                  <td className="py-4 px-6 font-sans tabular-nums font-bold text-sm text-on-surface whitespace-nowrap">
                    {item.dateDisplay || item.date}
                  </td>

                  {/* DAY */}
                  <td className="py-4 px-6 font-medium text-xs text-slate-700 whitespace-nowrap">
                    {item.day || '—'}
                  </td>

                  {/* TYPE */}
                  <td className="py-4 px-6 text-center whitespace-nowrap">
                    <span className="inline-flex items-center justify-center rounded-full bg-surface-container-high px-2.5 py-0.5 text-[11px] font-bold text-slate-700">
                      {item.type || 'Public Holiday'}
                    </span>
                  </td>

                  {/* STATUS */}
                  <td className="py-4 px-6 text-center whitespace-nowrap">
                    <span
                      className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                        isUpcoming
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}
                    >
                      {item.status || 'Active'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
